import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import HelioLogoMark from "@/components/HelioLogoMark";
import HeaderAuth from "@/components/HeaderAuth";
import SupportPhoneLink from "@/components/SupportPhoneLink";
import { trackCtaClick } from "@/analytics";
import { useNavTone, type NavTone } from "@/hooks/useNavTone";
import { BRAND } from "@/constants/brand";
import { cn } from "@/lib/utils";

const SECTION_LINKS = [
  { label: "How it works", hash: "#how-it-works" },
  { label: "Technology", hash: "#brain" },
  { label: "Features", hash: "#features" },
  { label: "Why Powlo", hash: "#trust" },
] as const;

export default function SiteNav({ defaultTone = "light" }: { defaultTone?: NavTone }) {
  const [location, navigate] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navTone = useNavTone(defaultTone);
  const isDarkSection = navTone === "dark";
  const barColor = isDarkSection ? BRAND.lime : BRAND.navy;
  const logoColor = isDarkSection ? BRAND.navy : BRAND.lime;
  const pathname = typeof window !== "undefined" ? window.location.pathname : location;
  const isBlog = pathname === "/blog" || pathname.startsWith("/blog/");
  const signInClassName = cn(
    "rounded-full px-6 py-[15px] min-h-[44px] text-sm font-medium whitespace-nowrap hover:brightness-95 transition-[filter,background-color,color] duration-300",
    isDarkSection ? "bg-[#061525] text-[#D4FF00]" : "bg-[#D4FF00] text-[#061525]"
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const desktopLinkClass = (active = false) =>
    cn(
      "transition-colors no-underline whitespace-nowrap",
      isDarkSection ? "hover:text-[#061525]" : "hover:text-white",
      active && (isDarkSection ? "text-[#061525] font-medium" : "text-white font-medium")
    );

  const mobileLinkClass = (active = false) =>
    cn(
      "text-sm transition-colors py-3 min-h-[44px] flex items-center border-b",
      isDarkSection
        ? "text-[#061525]/70 hover:text-[#061525] border-black/10"
        : "text-white/60 hover:text-white border-white/10",
      active && (isDarkSection ? "text-[#061525] font-medium" : "text-white font-medium")
    );

  return (
    <div className="fixed top-5 left-0 right-0 z-50 flex justify-center">
      <div className="w-full px-5 sm:px-[60px]">
        <nav
          className={cn(
            "relative w-full flex items-center justify-between gap-3 rounded-full backdrop-blur-[16px] transition-[background-color,border-color,box-shadow,color] duration-300",
            isDarkSection ? "border-black/10" : "border-white/10"
          )}
          style={{
            backgroundColor: barColor,
            padding: "12px 12px 12px 22px",
            boxShadow:
              scrolled || mobileMenuOpen
                ? isDarkSection
                  ? "0 12px 36px rgba(0,0,0,0.18)"
                  : "0 12px 36px rgba(0,0,0,0.35)"
                : isDarkSection
                  ? "0 8px 30px rgba(0,0,0,0.12)"
                  : "0 8px 30px rgba(0,0,0,0.25)",
          }}
        >
          <div className="flex items-center gap-2 shrink-0">
            <HelioLogoMark
              className="transition-colors duration-300"
              style={{ color: logoColor }}
            />
          </div>
          <div
            className={cn(
              "hidden xl:flex flex-1 justify-center items-center gap-6 text-[14.5px] min-w-0 transition-colors duration-300",
              isDarkSection ? "text-[#061525]/70" : "text-white/70"
            )}
          >
            {SECTION_LINKS.map((item) => (
              <a key={item.hash} href={`/${item.hash}`} className={desktopLinkClass()}>
                {item.label}
              </a>
            ))}
            <Link href="/blog" className={desktopLinkClass(isBlog)}>
              Insights
            </Link>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden xl:block">
              <HeaderAuth tone={isDarkSection ? "light" : "dark"} signInClassName={signInClassName} />
            </div>
            <button
              type="button"
              onClick={() => {
                trackCtaClick("nav_get_a_plan");
                navigate("/plan?entry=bill");
              }}
              className="hidden xl:inline-flex rounded-full px-6 py-[15px] min-h-[44px] text-sm font-medium whitespace-nowrap hover:brightness-95 transition-[filter] duration-300"
              style={{ backgroundColor: logoColor, color: barColor }}
            >
              See My Powtential&trade;
            </button>
            <button
              className="xl:hidden flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full transition-colors duration-300"
              style={{ color: logoColor }}
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-[35px] w-[35px]" /> : <Menu className="h-[35px] w-[35px]" />}
            </button>
          </div>

          {mobileMenuOpen ? (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
              className={cn(
                "xl:hidden absolute top-[calc(100%+10px)] left-0 right-0 rounded-[24px] backdrop-blur-[16px] px-5 py-4 flex flex-col gap-1 transition-colors duration-300",
                isDarkSection
                  ? "border border-black/10 shadow-[0_12px_36px_rgba(0,0,0,0.18)]"
                  : "border border-white/10 shadow-[0_12px_36px_rgba(0,0,0,0.35)]"
              )}
              style={{ backgroundColor: barColor }}
            >
              {SECTION_LINKS.map((item) => (
                <a
                  key={item.hash}
                  href={`/${item.hash}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={mobileLinkClass()}
                >
                  {item.label}
                </a>
              ))}
              <Link href="/blog" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass(isBlog)}>
                Insights
              </Link>
              <div
                className={cn(
                  "w-full py-3 min-h-[44px] flex items-center border-b",
                  isDarkSection ? "border-black/10" : "border-white/10"
                )}
              >
                <HeaderAuth
                  tone={isDarkSection ? "light" : "dark"}
                  signInClassName={cn(signInClassName, "w-full flex items-center justify-center")}
                />
              </div>
              <SupportPhoneLink
                trackName="mobile_call"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "w-full py-3 min-h-[44px] text-sm transition-colors border-b",
                  isDarkSection
                    ? "text-[#061525]/70 hover:text-[#061525] border-black/10"
                    : "text-white/60 hover:text-white border-white/10"
                )}
              />
              <button
                type="button"
                className="rounded-full w-full py-3 min-h-[44px] mt-2 text-sm font-medium hover:brightness-95 transition-[filter] duration-300"
                style={{ backgroundColor: logoColor, color: barColor }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackCtaClick("mobile_get_a_plan");
                  navigate("/plan?entry=bill");
                }}
              >
                See My Powtential&trade;
              </button>
            </motion.div>
          ) : null}
        </nav>
      </div>
    </div>
  );
}
